import {useState, useCallback} from 'react';
import {RelayEnvironment} from '../../../graphql/forecastRelay';
import UploadAttachmentMutation from '../../../../../mutations/ticketing/tickets/uploadAttachmentMutation';

const MAX_FILE_SIZE = 25 * 1024 * 1024;

export interface PendingAttachment {
	uploadId: string;
	storageKey: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
	progress: number;
}

interface UseAttachmentUploadResult {
	attachments: PendingAttachment[];
	error: string | null;
	upload: (ticketId: string, file: File) => Promise<void>;
	remove: (uploadId: string) => void;
	clear: () => void;
}

export function useAttachmentUpload(): UseAttachmentUploadResult {
	const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
	const [error, setError] = useState<string | null>(null);

	const upload = useCallback(async (ticketId: string, file: File) => {
		setError(null);
		if (file.size > MAX_FILE_SIZE) {
			setError(`"${file.name}" exceeds the 25 MB file size limit`);
			return;
		}
		const tempId = crypto.randomUUID();
		setAttachments(prev => [
			...prev,
			{
				uploadId: tempId,
				storageKey: '',
				filename: file.name,
				contentType: file.type || 'application/octet-stream',
				sizeBytes: file.size,
				progress: 0,
			},
		]);

		try {
			const attachment = await new Promise<{
				id: string;
				filename: string;
				contentType: string;
				sizeBytes: number;
				storageKey: string | null;
			}>((resolve, reject) => {
				UploadAttachmentMutation.commit(
					RelayEnvironment.getInstance(),
					{ticketId, filename: file.name, contentType: file.type || 'application/octet-stream'},
					response => {
						const a = response.uploadAttachment?.attachment;
						if (!a) {
							reject(new Error('Upload failed'));
							return;
						}
						resolve(a);
					},
					err => reject(err),
					{file}
				);
			});

			setAttachments(prev =>
				prev.map(a =>
					a.uploadId === tempId
						? {
								uploadId: attachment.id,
								// The reply path reads blobs by storage_key, so carry the
								// real storageKey here — NOT the row id. (Sending the row id
								// made SendReply 400 with "attachment not found".)
								storageKey: attachment.storageKey ?? '',
								filename: attachment.filename,
								contentType: attachment.contentType,
								sizeBytes: attachment.sizeBytes,
								progress: 100,
						  }
						: a
				)
			);
		} catch (err) {
			setAttachments(prev => prev.filter(a => a.uploadId !== tempId));
			setError(err instanceof Error ? err.message : 'Upload failed');
		}
	}, []);

	const remove = useCallback((uploadId: string) => {
		setAttachments(prev => prev.filter(a => a.uploadId !== uploadId));
	}, []);

	const clear = useCallback(() => {
		setAttachments([]);
		setError(null);
	}, []);

	return {attachments, error, upload, remove, clear};
}
